import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  Radio,
  RadioGroup,
  Textarea,
  useToast,
  Flex,
  Input,
  Divider,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { monthToStartDate, monthToEndDate, dateToMonth } from '../../utils/monthRange';
import StatusHistoryTimeline from '../Tags/StatusHistoryTimeline';

// EG-24: o fluxo de auditoria (EG-22) é o ATIVO; os status antigos permanecem
// apenas para registros legados (coexistência).
const STATUS_OPTIONS = {
  'não enviado': { value: 'NÃO ENVIADO', label: 'NÃO ENVIADO' },
  // Fluxo atual (auditoria)
  'em auditoria': { value: 'EM AUDITORIA', label: 'EM AUDITORIA' },
  'em ajustes': { value: 'EM AJUSTES', label: 'EM AJUSTES' },
  'em conferência final': {
    value: 'EM CONFERÊNCIA FINAL',
    label: 'EM CONFERÊNCIA FINAL',
  },
  auditado: { value: 'AUDITADO', label: 'AUDITADO' },
};

// EG-24: transições guiadas — a partir do status atual, só as ações válidas.
// Evita estados inconsistentes (voltar etapa, setar "não enviado", mexer em
// auditado). Status legados são tratados como equivalentes no fluxo de auditoria.
const TRANSITIONS = {
  'em auditoria': ['em ajustes', 'auditado'],
  'em ajustes': ['em conferência final', 'auditado'],
  'em conferência final': ['em ajustes', 'auditado'],
  'em avaliação': ['em ajustes', 'auditado'],
  'aguardando adequações': ['em conferência final', 'auditado'],
  'em revisão': ['em ajustes', 'auditado'],
  // EG-27: auditado pode regredir p/ "em ajustes" (homologação). Backend restringe
  // a Admin do painel (MASTER) e Auditor (AVALIADOR) e preserva a pontuação.
  auditado: ['em ajustes'],
  // Terminais / iniciais: nenhuma alteração manual.
  avaliado: [],
  'não enviado': [],
};

// Diagnóstico (EG-38): não tem lista livre de status — só 2 ações do avaliador,
// que o backend traduz em `decision: 'adjust' | 'complete'`
// (`diagnostic_status_control.service.ts#review`). Mesmo shape {value, label}
// do STATUS_OPTIONS pra reusar a mesma renderização de RadioGroup abaixo.
const DIAGNOSTIC_ACTIONS = {
  adjust: { value: 'adjust', label: 'DEVOLVER PARA AJUSTES' },
  complete: { value: 'complete', label: 'CONCLUIR AVALIAÇÃO' },
};

// Espelha TRANSITIONS: ações válidas a partir do status atual do diagnóstico.
const DIAGNOSTIC_TRANSITIONS = {
  'em avaliação': ['adjust', 'complete'],
  // 'avaliado' pode reabrir (mesma regra do 'auditado' -> 'em ajustes' do certificado).
  avaliado: ['adjust'],
  // Vez da empresa (reenviar) ou ainda não enviado: nenhuma ação manual.
  'em ajustes': [],
  'não enviado': [],
};

const EditStatusModal = ({
  isOpen,
  onClose,
  currentStatus = 'não enviado',
  onStatusUpdate,
  certificateStatusControlId,
  diagnosticStatusControlId,
  kind = 'certificate',
  control,
}) => {
  const toast = useToast();
  const { user } = useSSO();
  const [status, setStatus] = useState('');
  const [motive, setMotive] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // EG-27 (rework): janela de validade POR SELO por empresa (override do consultor).
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [validFrom2, setValidFrom2] = useState('');
  const [validUntil2, setValidUntil2] = useState('');
  const [isSavingValidity, setIsSavingValidity] = useState(false);

  const isDiagnostic = kind === 'diagnostic';
  const controlId = isDiagnostic ? diagnosticStatusControlId : certificateStatusControlId;

  // Ações válidas a partir do status atual.
  const allowedOptions = useMemo(() => {
    if (isDiagnostic) {
      const allowed = DIAGNOSTIC_TRANSITIONS[(currentStatus || '').toLowerCase()] || [];
      return allowed.map((key) => DIAGNOSTIC_ACTIONS[key]).filter(Boolean);
    }
    const allowed = TRANSITIONS[(currentStatus || '').toLowerCase()] || [];
    return allowed.map((key) => STATUS_OPTIONS[key]).filter(Boolean);
  }, [currentStatus, isDiagnostic]);

  // Ao abrir, seleciona a 1ª ação válida, limpa o motivo e carrega a validade.
  // validUntil/validFrom vêm em camelCase (TypeORM); date (YYYY-MM-DD) → mês/ano.
  useEffect(() => {
    if (isOpen) {
      setStatus(allowedOptions[0]?.value || '');
      setMotive('');
      setValidFrom(dateToMonth(control?.validFrom));
      setValidUntil(dateToMonth(control?.validUntil));
      setValidFrom2(dateToMonth(control?.validFrom2));
      setValidUntil2(dateToMonth(control?.validUntil2));
    }
  }, [isOpen, allowedOptions, control]);

  const handleSaveValidity = async () => {
    if (!certificateStatusControlId) return;
    try {
      setIsSavingValidity(true);
      await baseService.patch(
        `/certificate-status-control/${certificateStatusControlId}/validity`,
        {
          valid_from: validFrom ? monthToStartDate(validFrom) : null,
          valid_until: validUntil ? monthToEndDate(validUntil) : null,
          valid_from_2: validFrom2 ? monthToStartDate(validFrom2) : null,
          valid_until_2: validUntil2 ? monthToEndDate(validUntil2) : null,
        }
      );
      toast({ title: 'Validade atualizada com sucesso', status: 'success' });
      onStatusUpdate?.();
    } catch (error) {
      toast({
        title: 'Erro ao atualizar a validade',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
    } finally {
      setIsSavingValidity(false);
    }
  };

  const handleSave = async () => {
    if (!controlId) {
      toast({
        title: 'Erro ao atualizar status',
        description: 'ID do controle de status não encontrado',
        status: 'error',
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (isDiagnostic) {
        // EG-38: diagnóstico não tem PATCH de status livre — é sempre uma
        // decisão do avaliador (ver diagnostic_status_control.service.ts#review).
        await baseService.patch(`/diagnostic-status-control/${controlId}/review`, {
          decision: status,
          motive: motive,
          userId: user?.user?.id_user,
        });
        toast({ title: 'Status atualizado com sucesso', status: 'success' });
        onStatusUpdate?.();
        onClose();
        return;
      }

      const response = await baseService.patch(
        `/certificate-status-control/${controlId}`,
        {
          status: status,
          motive: motive,
          // EG-24: registra qual papel alterou o status no histórico.
          userId: user?.user?.id_user,
        }
      );

      if (response.status === 200) {
        toast({
          title: 'Status atualizado com sucesso',
          status: 'success',
        });
        onStatusUpdate?.();
        onClose();
      }
    } catch (error) {
      toast({
        title: 'Erro ao atualizar status',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setStatus(allowedOptions[0]?.value || '');
    setMotive('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered>
      <ModalOverlay />
      <ModalContent maxW='545px' p='32px'>
        <Text fontSize='24px' fontWeight={700} mb='30px'>
          {isDiagnostic ? 'Editar status do diagnóstico' : 'Editar status da certificação'}
        </Text>
        <Text fontSize='14px' fontWeight='normal'>
          Selecione o novo status e descreva o motivo para alteração:
        </Text>

        <ModalBody p='24px 0'>
          {allowedOptions.length === 0 ? (
            <Box
              bg='#F9F9F9'
              borderRadius='8px'
              p='16px'
              mb='24px'
              color='#5C6066'
              fontSize='14px'
            >
              Este status não permite alteração manual.
            </Box>
          ) : (
            <RadioGroup value={status} onChange={setStatus} mb='24px'>
              <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
                {allowedOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    size='lg'
                    colorScheme='blue'
                  >
                    {option.label}
                  </Radio>
                ))}
              </Flex>
            </RadioGroup>
          )}

          <Box mb='16px'>
            <Text mb='8px' fontSize='12px'>
              Motivo
            </Text>
            <Textarea
              value={motive}
              onChange={(e) => setMotive(e.target.value)}
              placeholder='Digite o motivo da alteração do status'
              bg='#F9F9F9'
              minH='120px'
              maxLength={255}
            />
            <Text fontSize='12px' color='#9CA0A6' textAlign='right' mt='1'>
              {motive.length}/255
            </Text>
          </Box>

          <Box mt='20px'>
            <Text fontSize='14px' color='#EA582B'>
              <Text as='span' fontWeight={600}>
                ATENÇÃO:
              </Text>{' '}
              ao alterar o status {isDiagnostic ? 'do diagnóstico' : 'da certificação'}, o
              avaliador responsável NÃO será notificado da mudança.
            </Text>
          </Box>

          {/* "A linha dos status": histórico completo (quem, quando, motivo) que
              a API já guarda mas nenhuma tela mostrava até aqui. */}
          <Divider my='24px' />
          <Box>
            <Text fontSize='16px' fontWeight={700} mb='12px'>
              Histórico de status
            </Text>
            <StatusHistoryTimeline
              kind={isDiagnostic ? 'diagnostic' : 'certificate'}
              controlId={controlId}
            />
          </Box>

          {/* EG-27: janela de validade por empresa (independente do status) — só
              existe pra certificação. */}
          {!isDiagnostic && (
          <>
          <Divider my='24px' />
          <Box>
            <Text fontSize='16px' fontWeight={700} mb='4px'>
              Validade do certificado (por empresa)
            </Text>
            <Text fontSize='12px' color='#5C6066' mb='12px'>
              Período mês/ano por selo. Preencha para sobrescrever o período
              padrão da certificação. Exibido junto ao selo da empresa.
            </Text>

            {[
              {
                label: 'Selo 1',
                from: validFrom,
                until: validUntil,
                setFrom: setValidFrom,
                setUntil: setValidUntil,
              },
              {
                label: 'Selo 2',
                from: validFrom2,
                until: validUntil2,
                setFrom: setValidFrom2,
                setUntil: setValidUntil2,
              },
            ].map((seal) => (
              <Box key={seal.label} mb='12px'>
                <Text fontSize='13px' fontWeight={600} color='#5C6066' mb='6px'>
                  {seal.label}
                </Text>
                <Flex gap='12px' align='flex-end' flexWrap='wrap'>
                  <Box>
                    <Text fontSize='12px' mb='4px'>
                      Início (mês/ano)
                    </Text>
                    <Input
                      type='month'
                      w='170px'
                      value={seal.from}
                      onChange={(e) => seal.setFrom(e.target.value)}
                    />
                  </Box>
                  <Box>
                    <Text fontSize='12px' mb='4px'>
                      Fim (mês/ano)
                    </Text>
                    <Input
                      type='month'
                      w='170px'
                      value={seal.until}
                      onChange={(e) => seal.setUntil(e.target.value)}
                    />
                  </Box>
                </Flex>
              </Box>
            ))}

            <Button
              variant='primary'
              size='medium'
              mt='4px'
              onClick={handleSaveValidity}
              isLoading={isSavingValidity}
            >
              Salvar validade
            </Button>
          </Box>
          </>
          )}
        </ModalBody>

        <ModalFooter p={0} mt='24px'>
          <Flex gap='24px' w='full'>
            <Button variant='cancel' size='large' w='100%' height='56px' onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              variant='primary'
              size='large'
              w='100%'
              height='56px'
              onClick={handleSave}
              isLoading={isSubmitting}
              isDisabled={!status || !motive.trim() || allowedOptions.length === 0}
            >
              Salvar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditStatusModal;